import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContainer } from './ToastContainer';

const ToastContext = createContext(null);

let toastId = 0;

export const ToastProvider = ({ children, maxToasts = 3, position = 'top-right' }) => {
  const [toasts, setToasts] = useState([]);
  
  const addToast = useCallback((options) => {
    const id = ++toastId;
    const toast = {
      id,
      variant: options.variant || 'info',
      title: options.title,
      message: options.message,
      duration: options.duration ?? 5000,
      action: options.action,
      isExiting: false,
    };
    
    setToasts((prev) => {
      const newToasts = [...prev, toast];
      // Keep only the latest maxToasts
      return newToasts.slice(-maxToasts);
    });
    
    return id;
  }, [maxToasts]);
  
  const removeToast = useCallback((id) => {
    // Mark as exiting first for animation
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, isExiting: true } : toast
      )
    );
    
    // Remove after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 200);
  }, []);
  
  const toast = {
    success: (message, options = {}) =>
      addToast({ variant: 'success', message, ...options }),
    error: (message, options = {}) =>
      addToast({ variant: 'error', message, duration: 10000, ...options }),
    warning: (message, options = {}) =>
      addToast({ variant: 'warning', message, ...options }),
    info: (message, options = {}) =>
      addToast({ variant: 'info', message, ...options }),
    custom: (options) => addToast(options),
  };
  
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} position={position} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export default ToastProvider;
